/**
 * pendingPlanHelpers — keep the Plan-state mutations OUT of the hook file so
 * usePendingChannelDrops.ts stays under the 200-line FE limit.
 *
 * The "reorderPlan per channel" model lives in channelReorderPlan.ts (pure
 * helpers). This module wires those helpers into React state setters so the
 * hook can stay declarative.
 *
 * Plans are LAZILY seeded from the TanStack QueryClient cache the first time
 * a channel is touched, so the hook never depends on a `snapshot` prop drilled
 * from the workspace (race-prone — the live bug).
 */
import type { Dispatch, SetStateAction } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import {
  applyDeleteToPlan,
  applyInsertToPlan,
  applyReorderToPlan,
  getChannelSnapshotFromCache,
  type ChannelPlanItem,
  type ChannelReorderPlans,
} from './channelReorderPlan'
import type { AddToProductImage } from '../types'

export interface PlanContext {
  setPlans: Dispatch<SetStateAction<ChannelReorderPlans>>
  qc: QueryClient
  productId: string
}

function ensurePlan(
  plans: ChannelReorderPlans,
  channelId: string,
  qc: QueryClient,
  productId: string,
): ChannelPlanItem[] {
  const existing = plans.get(channelId)
  if (existing) return existing
  return getChannelSnapshotFromCache(qc, productId, channelId)
}

/**
 * Stage a new image at the user's target slot in the channel plan. If no plan
 * exists yet, seed it from the QueryCache snapshot first (lazy init).
 */
export function planInsert(
  ctx: PlanContext,
  channelId: string,
  targetSlot: number,
  image: AddToProductImage,
  tempKey: string,
): void {
  ctx.setPlans((prev) => {
    const base = ensurePlan(prev, channelId, ctx.qc, ctx.productId)
    const next = new Map(prev)
    next.set(channelId, applyInsertToPlan(base, targetSlot, image, tempKey))
    return next
  })
}

/** Move an existing slot in the channel plan (same-channel reorder). */
export function planReorder(
  ctx: PlanContext,
  channelId: string,
  fromSlot: number,
  toSlot: number,
): void {
  ctx.setPlans((prev) => {
    const base = ensurePlan(prev, channelId, ctx.qc, ctx.productId)
    const reordered = applyReorderToPlan(base, fromSlot, toSlot)
    if (reordered === base && !prev.has(channelId)) return prev
    const next = new Map(prev)
    next.set(channelId, reordered)
    return next
  })
}

/** Drop an item from the channel plan (also seeds it from cache if needed). */
export function planDelete(
  ctx: PlanContext,
  channelId: string,
  slot: number,
): void {
  ctx.setPlans((prev) => {
    const base = ensurePlan(prev, channelId, ctx.qc, ctx.productId)
    const next = new Map(prev)
    next.set(channelId, applyDeleteToPlan(base, slot))
    return next
  })
}
